import * as React from 'react';
import { Link } from 'react-router-dom';

import { Discount } from './types';
import { Spinner } from '../assets/Spinner';
import { ChangeDiscountStatus } from '../component/ChangeDiscountStatus';
import { Table } from '../component/Table';
import { TableTools, FilterConfig } from '../component/TableTools';
import { discountTypeOptions, discountLimitTypeOptions } from '../dropdownOptions';
import { useFetchWithFilters } from '../hooks/useFetchWithPagination';
import { DiscountsHeader } from '../tableHeaders';
import { formatDateTime, getCountryFlag } from '../utils';

const discountFilters: FilterConfig[] = [
  {
    key: 'discount_type',
    label: 'Discount Type',
    type: 'multiselect',
    options: discountTypeOptions,
    placeholder: 'Select discount types',
  },
  {
    key: 'value',
    label: 'Value Range',
    type: 'numberrange',
    placeholder: 'value',
  },
  {
    key: 'limit_type',
    label: 'Limit Type',
    type: 'multiselect',
    options: discountLimitTypeOptions,
    placeholder: 'Select limit types',
  },
];

export const Discounts = () => {
  const mapDiscount = (discount: Discount) => {
    const orderTypes = {
      Trial: discount.order_trial ? '✅' : '❌',
      Subscription: discount.order_subscription ? '✅' : '❌',
      Onetime: discount.order_onetime ? '✅' : '❌',
    };
    return {
      'Discount Code': (
        <Link to={`/discounts/${discount.id}`} className="text-blue-500 hover:underline">
          {discount.code}
        </Link>
      ),
      'Discount Type': discount.discount_type.replace(/ Discount$/, ''),
      Status: (
        <ChangeDiscountStatus
          isActive={discount.is_active}
          discountId={discount.id}
          discountCode={discount.code}
          reFetch={reFetch}
        />
      ),
      Value: discount.value,
      'Start date': `${formatDateTime(discount.available_start_date)}`,
      'End date': `${formatDateTime(discount.available_end_date)}`,
      'Limit type': discount.limit_type,
      'Limit value': discount.limit_value ?? '--',
      'Order Types': [
        `${orderTypes.Trial} Trial`,
        `${orderTypes.Subscription} Subscription`,
        `${orderTypes.Onetime} Onetime`,
      ],
      'Created date': `${formatDateTime(discount.created)}`,
      'Eligible Country': getCountryFlag(discount.eligible_country) || '--',
    };
  };

  const {
    currentData: data,
    renderPagination,
    reFetch,
    searchBy,
    applyFilters,
    clearFilters,
    filters,
    searchText,
    searchField,
    isLoading,
    ...forPagination
  } = useFetchWithFilters<Discount>({
    endpoint: '/discounts/',
    mapper: mapDiscount,
  });

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <div className="relative h-[calc(100vh-77px)] bg-slate50 p-8">
      <div className="flex items-center justify-between mb-3">
        <h1>Manage Discounts</h1>
      </div>
      <TableTools
        renderPagination={renderPagination}
        forPagination={forPagination}
        isLoading={isLoading}
        searchBy={searchBy}
        reFetch={reFetch}
        hasData={data.length > 0}
        createLabel="Create Discount"
        createLink="/discounts/create-discount"
        changelogLink="/discounts/changelog"
        filters={discountFilters}
        applyFilters={applyFilters}
        clearFilters={clearFilters}
        currentFilters={filters}
        currentSearchText={searchText}
        currentSearchField={searchField}
      >
        <Table header={DiscountsHeader} data={data} internalPagination={false} />
      </TableTools>
    </div>
  );
};
